import { Component, Inject } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../../../../environments/environment'
import { Store } from '@ngrx/store'
import { selectCaseId } from 'src/app/state/case/case.selectors'
import { take } from 'rxjs'
import { ToasterService } from 'src/app/shared/services/toaster.service'
import { BaseLayer } from 'src/app/shared/model/layers/base-layer'
import { viewerActions } from 'src/app/state/viewer/viewer.actions'
import { selectionActions } from 'src/app/state/selection/selection.actions'
import { LayerType } from 'src/app/shared/model/layers/layer-types'
import { SelectionLayer } from 'src/app/shared/model/layers/selection-layer'
import {
    selectAllSavedHierarchicalSelectionNodeItems,
    selectAllSavedUnsupervisedSelectionNodeItems,
    selectSavedFreeSelections,
} from 'src/app/state/selection/selection.selectors'
import { SelectionTreeItem, SelectionType } from 'src/app/state/selection/selection.state'

export interface RenameLayerDialogData {
    currentName: string
    layer: BaseLayer | SelectionLayer | undefined
    selectionType: SelectionType
}

@Component({
    selector: 'app-rename-layer-dialog',
    templateUrl: './rename-layer-dialog.component.html',
    styleUrls: ['./rename-layer-dialog.component.scss'],
})
export class RenameLayerDialogComponent {
    renameForm: FormGroup
    isSubmitting = false
    errorMessage = ''
    newName = ''

    readonly currentCaseId$ = this.store.select(selectCaseId)

    constructor(
        private formBuilder: FormBuilder,
        private http: HttpClient,
        private dialogRef: DialogRef<string>,
        private store: Store,
        private toaster: ToasterService,
        @Inject(DIALOG_DATA) public data: RenameLayerDialogData
    ) {
        this.renameForm = this.formBuilder.group({
            newName: [data.currentName, [Validators.required, Validators.minLength(1)]],
        })
    }

    onSubmit(): void {
        this.newName = this.renameForm.get('newName')?.value
        const layer = this.data.layer
        if (!layer) {
            this.renameHierarchicalSelectionRoot()
            return
        }

        if ((layer as SelectionLayer).type !== LayerType.Selection) {
            this.renameLayer()
        } else if (!(layer as SelectionLayer).groupName) {
            this.renameSelectionLayer()
        } else {
            this.renameHierarchicalSelectionLayer()
        }
    }

    onCancel(): void {
        this.dialogRef.close()
    }

    renameLayer(): void {
        if (this.renameForm.valid && this.data.layer?.nameOrDefault) {
            this.isSubmitting = true
            this.errorMessage = ''

            const payload = {
                currentName: this.data.currentName,
                newName: this.newName,
            }

            this.currentCaseId$.pipe(take(1)).subscribe(caseId => {
                this.http
                    .put(`${environment.apiUrl}/layers/${caseId}/rename`, payload, {
                        headers: {
                            'Content-Type': 'application/json',
                            accept: 'application/json',
                        },
                    })
                    .subscribe({
                        next: (res: any) => {
                            ;(this.data.layer as BaseLayer).setName(res.newName)

                            this.store.dispatch(
                                viewerActions.setLayerName({
                                    name: this.data.currentName,
                                    newName: res.newName,
                                })
                            )

                            if (res.newName !== this.newName) {
                                this.toaster.error(
                                    `saved as ${res.newName}`,
                                    'Layer name already exists'
                                )
                            }
                            this.dialogRef.close()
                        },
                        error: error => {
                            this.isSubmitting = false
                            this.toaster.error(error.error?.message, 'Failed to rename layer')
                        },
                    })
            })
        }
    }

    renameSelectionLayer(): void {
        this.store
            .select(selectSavedFreeSelections)
            .pipe(take(1))
            .subscribe(selections => {
                if (selections.has(this.newName)) {
                    this.toaster.error(this.newName, 'Selection name already exists')
                    return
                }

                this.store.dispatch(
                    selectionActions.renameFreeSelection({
                        name: this.data.currentName,
                        newName: this.newName,
                    })
                )
                ;(this.data.layer as SelectionLayer).setName(this.newName)
                this.dialogRef.close(this.newName)
            })
    }

    renameHierarchicalSelectionLayer(): void {
        const rootName = (this.data.layer as SelectionLayer).groupName!
        const nodeName = this.data.currentName
        this.store
            .select(
                this.data.selectionType === SelectionType.Unsupervised
                    ? selectAllSavedUnsupervisedSelectionNodeItems
                    : selectAllSavedHierarchicalSelectionNodeItems
            )
            .pipe(take(1))
            .subscribe(state => {
                if (
                    state
                        .find(g => g.groupName === rootName)!
                        .tree.find(t => t.name === this.newName)
                ) {
                    this.toaster.error(this.newName, 'Selection name already exists')
                    return
                }
                if (this.data.selectionType === SelectionType.Unsupervised) {
                    this.store.dispatch(
                        selectionActions.renameUnsupervisedSelection({
                            rootName: rootName,
                            name: nodeName,
                            newName: this.newName,
                        })
                    )
                } else {
                    this.store.dispatch(
                        selectionActions.renameHierarchicalSelection({
                            rootName: rootName,
                            name: nodeName,
                            newName: this.newName,
                        })
                    )
                }
                ;(this.data.layer as SelectionLayer).setName(this.newName)
                this.dialogRef.close()
            })
    }

    renameHierarchicalSelectionRoot(): void {
        this.store
            .select(selectAllSavedHierarchicalSelectionNodeItems)
            .pipe(take(1))
            .subscribe((groups: { groupName: string; tree: SelectionTreeItem[] }[]) => {
                if (groups.find(g => g.groupName === this.newName)) {
                    this.toaster.error(this.newName, 'Selection name already exists')
                    return
                }

                if (this.data.selectionType === SelectionType.Unsupervised) {
                    this.store.dispatch(
                        selectionActions.renameUnsupervisedSelectionRoot({
                            name: this.data.currentName,
                            newName: this.newName,
                        })
                    )
                } else {
                    this.store.dispatch(
                        selectionActions.renameHierarchicalSelectionRoot({
                            name: this.data.currentName,
                            newName: this.newName,
                        })
                    )
                }
                this.dialogRef.close(this.newName)
            })
    }
}
